import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, UserPlus, UserMinus, Home, User, Phone, Calendar, Upload, Pencil, X } from 'lucide-react';
import RupiahIcon from '@/components/RupiahIcon';
import axios from 'axios';
import { isFinanceAccount, currentAccount } from '@/lib/accountPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${BACKEND_URL}${url}`;

const OccupancyManagement = () => {
  const { propertyName, ownerEmail } = useParams();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');
  const account = currentAccount();
  const canVerify = isFinanceAccount(account);
  const canManualCheckout = canVerify || account?.role === 'superadmin';
  
  // Form states
  const [checkInForm, setCheckInForm] = useState({
    room_number: '',
    tenant_full_name: '',
    tenant_phone: '',
    tenant_ktp_photo_url: '',
    check_in_date: new Date().toISOString().split('T')[0],
    room_price: '',
    jumlah_penghuni: 1,
    laundry: false,
    parkir: false,
    cleaning: false,
    laundry_price: '',
    parkir_price: '',
    cleaning_price: '',
  });
  const [checkInKtpFile, setCheckInKtpFile] = useState(null);
  const [checkInDepositFile, setCheckInDepositFile] = useState(null);
  const [coTenants, setCoTenants] = useState([]);
  const [coKtpFiles, setCoKtpFiles] = useState({});

  const [checkOutForm, setCheckOutForm] = useState({
    room_number: '',
    check_out_date: new Date().toISOString().split('T')[0]
  });
  
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tenantCredentials, setTenantCredentials] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editKtpFile, setEditKtpFile] = useState(null);
  const [editDepositFile, setEditDepositFile] = useState(null);
  const [editCoTenants, setEditCoTenants] = useState([]);
  const [editCoKtpFiles, setEditCoKtpFiles] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [scheduledCheckins, setScheduledCheckins] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [propertyRes, roomsRes, eventsRes, scheduledRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/properties`, config),
        axios.get(`${BACKEND_URL}/api/admin/room-status/${propertyName}?owner_email=${ownerEmail}`, config),
        axios.get(`${BACKEND_URL}/api/admin/occupancy-events/${propertyName}?owner_email=${ownerEmail}`, config),
        axios.get(`${BACKEND_URL}/api/admin/scheduled-checkins?owner_email=${ownerEmail}`, config).catch(() => ({ data: [] }))
      ]);

      const prop = propertyRes.data.find(p => p.name === propertyName && p.owner_email === ownerEmail);
      setProperty(prop);
      setRooms(roomsRes.data);
      setEvents(eventsRes.data);
      setScheduledCheckins((scheduledRes.data || []).filter(sc => sc.property_name === propertyName));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // For now, we'll use a simple base64 encoding
    // In production, you'd upload to cloud storage
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCheckInForm({ ...checkInForm, tenant_ktp_photo_url: reader.result });
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Gagal upload foto');
      setUploading(false);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!canVerify) return;
    setError('');
    setSuccess('');

    if (!checkInForm.room_number) {
      setError('Pilih kamar terlebih dahulu');
      return;
    }

    if (!checkInForm.room_price) {
      setError('Masukkan harga kamar');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('property_name', propertyName);
      fd.append('room_number', checkInForm.room_number);
      fd.append('tenant_full_name', checkInForm.tenant_full_name);
      fd.append('tenant_phone', checkInForm.tenant_phone);
      fd.append('check_in_date', checkInForm.check_in_date);
      fd.append('room_price', parseFloat(checkInForm.room_price));
      fd.append('laundry', checkInForm.laundry);
      fd.append('parkir', checkInForm.parkir);
      fd.append('cleaning', checkInForm.cleaning);
      fd.append('laundry_price', checkInForm.laundry ? parseFloat(checkInForm.laundry_price)||0 : 0);
      fd.append('parkir_price', checkInForm.parkir ? parseFloat(checkInForm.parkir_price)||0 : 0);
      fd.append('cleaning_price', checkInForm.cleaning ? parseFloat(checkInForm.cleaning_price)||0 : 0);
      if (checkInKtpFile) fd.append('ktp', checkInKtpFile);
      if (checkInDepositFile) fd.append('bukti_deposit', checkInDepositFile);
      if (coTenants.length > 0) {
        fd.append('co_tenants', JSON.stringify(coTenants.map(ct => ({ nama: ct.nama, phone: ct.phone, ktp_url: null }))));
        coTenants.forEach((_, i) => { if (coKtpFiles[i]) fd.append(`co_ktp_${i+1}`, coKtpFiles[i]); });
      }

      const today = new Date().toISOString().split('T')[0];
      const isFuture = checkInForm.check_in_date > today;
      const endpoint = isFuture
        ? `${BACKEND_URL}/api/admin/scheduled-checkins?owner_email=${ownerEmail}`
        : `${BACKEND_URL}/api/admin/check-in?owner_email=${ownerEmail}`;

      const response = await axios.post(endpoint, fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.tenant_credentials) {
        setTenantCredentials(response.data.tenant_credentials);
      }

      setSuccess(isFuture
        ? `Check-in dijadwalkan pada ${checkInForm.check_in_date}. Tenant akan otomatis aktif pada tanggal tersebut.`
        : 'Check-in berhasil dicatat!');
      fetchData();
      setCheckInKtpFile(null);
      setCheckInDepositFile(null);
      setCoTenants([]);
      setCoKtpFiles({});
      // Reset form
      setCheckInForm({
        room_number: '',
        tenant_full_name: '',
        tenant_phone: '',
        tenant_ktp_photo_url: '',
        jumlah_penghuni: 1,
        check_in_date: new Date().toISOString().split('T')[0],
        room_price: '',
        laundry: false,
        parkir: false,
        cleaning: false,
        laundry_price: '',
        parkir_price: '',
        cleaning_price: '',
      });

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal mencatat check-in');
    }
  };

  const handleCheckOut = async (e) => {
    e.preventDefault();
    if (!canManualCheckout) return;
    setError('');
    setSuccess('');

    if (!checkOutForm.room_number) {
      setError('Pilih kamar terlebih dahulu');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${BACKEND_URL}/api/admin/check-out?owner_email=${ownerEmail}`,
        {
          property_name: propertyName,
          room_number: checkOutForm.room_number,
          check_out_date: checkOutForm.check_out_date
        },
        config
      );

      setSuccess('Check-out berhasil dicatat!');
      fetchData();
      
      // Reset form
      setCheckOutForm({
        room_number: '',
        check_out_date: new Date().toISOString().split('T')[0]
      });
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal mencatat check-out');
    }
  };

  const openEditModal = (room) => {
    const t = room.tenant;
    setEditForm({
      tenant_full_name: t.tenant_name || '',
      tenant_phone: t.tenant_phone || '',
      event_date: t.check_in_date ? t.check_in_date.slice(0,10) : '',
      room_price: t.room_price || '',
      laundry: !!t.laundry,
      parkir: !!t.parkir,
      cleaning: !!t.cleaning,
      laundry_price: t.laundry_price || '',
      parkir_price: t.parkir_price || '',
      cleaning_price: t.cleaning_price || '',
      event_id: t.event_id || '',
    });
    setEditModal(room);
    setEditKtpFile(null);
    setEditDepositFile(null);
    setEditCoTenants(room.tenant?.co_tenants || []);
    setEditCoKtpFiles({});
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!canVerify) return;
    setEditSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      Object.keys(editForm).forEach(k => { if (k !== 'event_id') fd.append(k, editForm[k] ?? ''); });
      if (editKtpFile) fd.append('ktp', editKtpFile);
      if (editDepositFile) fd.append('bukti_deposit', editDepositFile);
      if (editCoTenants.length > 0) {
        fd.append('co_tenants', JSON.stringify(editCoTenants.map(ct => ({ nama: ct.nama, phone: ct.phone, ktp_url: ct.ktp_url||null }))));
        editCoTenants.forEach((_, i) => { if (editCoKtpFiles[i]) fd.append(`co_ktp_${i+1}`, editCoKtpFiles[i]); });
      }
      await axios.put(`${BACKEND_URL}/api/admin/check-in/${editForm.event_id}?owner_email=${ownerEmail}`,
        fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setSuccess('Data check-in berhasil diperbarui');
      setEditModal(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal memperbarui');
    } finally {
      setEditSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const availableRooms = rooms.filter(r => r.status === 'available');
  const occupiedRooms = rooms.filter(r => r.status === 'occupied');

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-isk-bg">
      {/* Header */}
      <div className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="mb-4 border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Admin
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold text-isk-text">{property?.name}</h1>
            <p className="text-isk-muted mt-1">{property?.location} • Check-in / Check-out Management</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-card rounded-2xl p-6" data-testid="total-rooms-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-isk-teal/10 flex items-center justify-center">
                <Home className="w-6 h-6 text-isk-teal" />
              </div>
            </div>
            <p className="text-sm text-isk-muted mb-1">Total Kamar</p>
            <p className="text-2xl font-bold text-isk-text">{rooms.length}</p>
          </div>

          <div className="glass-card rounded-2xl p-6" data-testid="occupied-rooms-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-sm text-isk-muted mb-1">Kamar Terisi</p>
            <p className="text-2xl font-bold text-red-600">{occupiedRooms.length}</p>
          </div>

          <div className="glass-card rounded-2xl p-6" data-testid="available-rooms-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Home className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-isk-muted mb-1">Kamar Tersedia</p>
            <p className="text-2xl font-bold text-green-600">{availableRooms.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <Button
            onClick={() => setActiveTab('status')}
            variant={activeTab === 'status' ? 'default' : 'outline'}
            className={`rounded-full px-6 ${activeTab === 'status' ? 'bg-isk-teal text-white' : 'border-isk-teal/30 text-isk-teal'}`}
            data-testid="tab-status"
          >
            Status Kamar
          </Button>
          {canVerify && <Button
            onClick={() => setActiveTab('checkin')}
            variant={activeTab === 'checkin' ? 'default' : 'outline'}
            className={`rounded-full px-6 ${activeTab === 'checkin' ? 'bg-isk-teal text-white' : 'border-isk-teal/30 text-isk-teal'}`}
            data-testid="tab-checkin"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Check-in
          </Button>}
          {canManualCheckout && <Button
            onClick={() => setActiveTab('checkout')}
            variant={activeTab === 'checkout' ? 'default' : 'outline'}
            className={`rounded-full px-6 ${activeTab === 'checkout' ? 'bg-isk-teal text-white' : 'border-isk-teal/30 text-isk-teal'}`}
            data-testid="tab-checkout"
          >
            <UserMinus className="w-4 h-4 mr-2" />
            Check-out
          </Button>}
          <Button
            onClick={() => setActiveTab('history')}
            variant={activeTab === 'history' ? 'default' : 'outline'}
            className={`rounded-full px-6 ${activeTab === 'history' ? 'bg-isk-teal text-white' : 'border-isk-teal/30 text-isk-teal'}`}
            data-testid="tab-history"
          >
            Riwayat
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'status' && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-isk-text mb-6">Status Semua Kamar</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rooms.map((room, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-xl border-2 ${
                    room.status === 'occupied' 
                      ? 'bg-red-50 border-red-200' 
                      : 'bg-green-50 border-green-200'
                  }`}
                  data-testid={`room-card-${room.room_number}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-isk-text">Kamar {room.room_number}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      room.status === 'occupied' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {room.status === 'occupied' ? 'Terisi' : 'Tersedia'}
                    </span>
                  </div>
                  <p className="text-sm text-isk-muted mb-2">Tipe: {room.room_type}</p>
                  
                  {room.tenant && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center text-sm text-isk-text mb-1">
                        <User className="w-4 h-4 mr-2 text-isk-muted" />
                        {room.tenant.full_name}
                      </div>
                      <div className="flex items-center text-sm text-isk-muted mb-1">
                        <Phone className="w-4 h-4 mr-2" />
                        {room.tenant.phone}
                      </div>
                      <div className="flex items-center text-sm text-isk-muted mb-1">
                        <Calendar className="w-4 h-4 mr-2" />
                        Masuk: {formatDate(room.tenant.check_in_date)}
                      </div>
                      {room.tenant.room_price && (
                        <div className="flex items-center text-sm text-isk-teal font-semibold">
                          <RupiahIcon className="w-4 h-4 mr-2" />
                          {formatCurrency(room.tenant.room_price)}/bulan
                        </div>
                      )}
                      {room.tenant.co_tenants && room.tenant.co_tenants.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                          <p className="text-xs text-isk-muted font-medium">Penghuni lain:</p>
                          {room.tenant.co_tenants.map((ct, i) => (
                            <div key={i} className="flex items-center text-xs">
                              <span className="text-isk-text">{ct.nama || '-'}{ct.status ? ` · ${ct.status}` : ''}{ct.phone ? ` · ${ct.phone}` : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {canVerify && !room.tenant.bukti_deposit_url && (
                        <label className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-300 bg-orange-50 text-orange-600 cursor-pointer text-xs hover:bg-orange-100 w-full justify-center">
                          <Upload className="w-3 h-3" /> Upload Bukti Deposit
                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={async e => {
                            const file = e.target.files?.[0]; if (!file) return;
                            const fd = new FormData();
                            fd.append('bukti_deposit', file);
                            try {
                              const token = localStorage.getItem('token');
                              await axios.put(`${BACKEND_URL}/api/admin/check-in/${room.tenant.event_id}?owner_email=${ownerEmail}`, fd,
                                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }});
                              setSuccess('Bukti deposit berhasil diupload');
                              fetchData();
                              setTimeout(() => setSuccess(''), 3000);
                            } catch { setError('Gagal upload bukti deposit'); }
                          }} />
                        </label>
                      )}
                      {canVerify && <Button size="sm" onClick={() => openEditModal(room)}
                        className="mt-2 w-full bg-white/80 hover:bg-white border border-gray-200 text-isk-text rounded-full text-xs h-7">
                        <Pencil className="w-3 h-3 mr-1" /> Edit Data Check-in
                      </Button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {canVerify && activeTab === 'checkin' && (
          <div className="glass-card rounded-2xl p-6 max-w-xl">
            <h3 className="text-xl font-bold text-isk-text mb-6 flex items-center">
              <UserPlus className="w-5 h-5 mr-2 text-isk-teal" />
              Form Check-in Tenant
            </h3>

            <form onSubmit={handleCheckIn} className="space-y-4" data-testid="checkin-form">
              <div className="space-y-2">
                <Label>Pilih Kamar *</Label>
                <Select
                  value={checkInForm.room_number}
                  onValueChange={(value) => setCheckInForm({ ...checkInForm, room_number: value })}
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="checkin-select-room">
                    <SelectValue placeholder="Pilih kamar tersedia" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRooms.length > 0 ? (
                      availableRooms.map((room, idx) => (
                        <SelectItem key={`available-${room.room_number}-${idx}`} value={room.room_number}>
                          Kamar {room.room_number} - {room.room_type}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>Tidak ada kamar tersedia</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant_name">Nama Lengkap Tenant *</Label>
                <Input
                  id="tenant_name"
                  type="text"
                  required
                  value={checkInForm.tenant_full_name}
                  onChange={(e) => setCheckInForm({ ...checkInForm, tenant_full_name: e.target.value })}
                  placeholder="Nama sesuai KTP"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="checkin-input-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant_phone">Nomor HP *</Label>
                <Input
                  id="tenant_phone"
                  type="tel"
                  required
                  value={checkInForm.tenant_phone}
                  onChange={(e) => setCheckInForm({ ...checkInForm, tenant_phone: e.target.value })}
                  placeholder="08xxxxxxxxxx"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="checkin-input-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="checkin_date">Tanggal Check-in *</Label>
                <Input
                  id="checkin_date"
                  type="date"
                  required
                  value={checkInForm.check_in_date}
                  onChange={(e) => setCheckInForm({ ...checkInForm, check_in_date: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="checkin-input-date"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="room_price">Harga Kamar per Bulan (Rp) *</Label>
                <Input
                  id="room_price"
                  type="number"
                  required
                  min="0"
                  value={checkInForm.room_price}
                  onChange={(e) => setCheckInForm({ ...checkInForm, room_price: e.target.value })}
                  placeholder="Contoh: 2000000"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="checkin-input-price"
                />
              </div>

              <div className="space-y-2">
                <Label>Fasilitas Tambahan</Label>
                <div className="space-y-2">
                  {[
                    { key: 'laundry', priceKey: 'laundry_price', label: 'Laundry' },
                    { key: 'parkir', priceKey: 'parkir_price', label: 'Parkir' },
                    { key: 'cleaning', priceKey: 'cleaning_price', label: 'Cleaning' },
                  ].map(({ key, priceKey, label }) => (
                    <div key={key} className={`rounded-xl border-2 transition-colors overflow-hidden ${checkInForm[key] ? 'border-isk-teal' : 'border-gray-200'}`}>
                      <label className={`flex items-center gap-3 p-3 cursor-pointer ${checkInForm[key] ? 'bg-isk-teal/10' : 'bg-white/60'}`}>
                        <input type="checkbox" checked={checkInForm[key]} onChange={e => setCheckInForm({ ...checkInForm, [key]: e.target.checked })} className="accent-isk-teal w-4 h-4" />
                        <span className={`text-sm font-medium ${checkInForm[key] ? 'text-isk-teal' : 'text-isk-muted'}`}>{label}</span>
                      </label>
                      {checkInForm[key] && (
                        <div className="px-3 pb-3 bg-isk-teal/5">
                          <Input
                            type="number" min="0"
                            value={checkInForm[priceKey]}
                            onChange={e => setCheckInForm({ ...checkInForm, [priceKey]: e.target.value })}
                            placeholder={`Harga ${label} / bulan (Rp)`}
                            className="glass-input border-2 focus:border-isk-teal rounded-xl text-sm"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {[
                { key: 'checkInKtpFile', setter: setCheckInKtpFile, file: checkInKtpFile, label: 'Foto KTP Tenant Utama' },
                { key: 'checkInDepositFile', setter: setCheckInDepositFile, file: checkInDepositFile, label: 'Bukti Deposit' },
              ].map(({ file, setter, label }) => (
                <div key={label} className="space-y-2">
                  <Label>{label}</Label>
                  <label className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed cursor-pointer text-sm transition-colors ${file ? 'border-isk-teal bg-isk-teal/5 text-isk-teal' : 'border-gray-200 text-isk-muted hover:bg-gray-50'}`}>
                    <Upload className="w-4 h-4 shrink-0" />
                    <span className="truncate">{file ? file.name : `Upload ${label}`}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setter(e.target.files?.[0]||null)} />
                  </label>
                </div>
              ))}

              <div className="space-y-2">
                <Label htmlFor="jumlah_penghuni">Jumlah Penghuni</Label>
                <Input
                  id="jumlah_penghuni"
                  type="number" min="1" max="10"
                  value={checkInForm.jumlah_penghuni}
                  onChange={e => {
                    const n = parseInt(e.target.value)||1;
                    setCheckInForm({...checkInForm, jumlah_penghuni: n});
                    setCoTenants(prev => {
                      const needed = n - 1;
                      if (needed <= 0) return [];
                      const next = [...prev];
                      while (next.length < needed) next.push({ nama: '', phone: '' });
                      return next.slice(0, needed);
                    });
                  }}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
              </div>

              {coTenants.length > 0 && (
                <div className="space-y-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <p className="text-sm font-semibold text-isk-text">Data Penghuni Tambahan</p>
                  {coTenants.map((ct, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white border border-blue-100 space-y-2">
                      <p className="text-xs font-medium text-isk-muted">Penghuni {i + 2}</p>
                      <Input placeholder="Nama lengkap *" value={ct.nama}
                        onChange={e => setCoTenants(prev => prev.map((c,j) => j===i ? {...c, nama: e.target.value} : c))}
                        className="rounded-xl text-sm" />
                      <Input placeholder="Nomor HP" value={ct.phone}
                        onChange={e => setCoTenants(prev => prev.map((c,j) => j===i ? {...c, phone: e.target.value} : c))}
                        className="rounded-xl text-sm" type="tel" />
                      <select
                        value={ct.status || ''}
                        onChange={e => setCoTenants(prev => prev.map((c,j) => j===i ? {...c, status: e.target.value} : c))}
                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-isk-teal focus:outline-none bg-white">
                        <option value="">Status / Hubungan</option>
                        <option value="Suami">Suami</option>
                        <option value="Istri">Istri</option>
                        <option value="Anak">Anak</option>
                        <option value="Keluarga">Keluarga</option>
                        <option value="Teman">Teman</option>
                        <option value="Lainnya">Lainnya</option>
                      </select>
                      <label className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed cursor-pointer text-xs transition-colors ${coKtpFiles[i] ? 'border-isk-teal bg-isk-teal/5 text-isk-teal' : 'border-gray-200 text-isk-muted hover:bg-gray-50'}`}>
                        <Upload className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{coKtpFiles[i] ? coKtpFiles[i].name : 'Upload foto KTP'}</span>
                        <input type="file" accept="image/*,.pdf" className="hidden"
                          onChange={e => setCoKtpFiles(prev => ({...prev, [i]: e.target.files?.[0]||null}))} />
                      </label>
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="submit"
                disabled={availableRooms.length === 0}
                className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full py-6 font-semibold shadow-lg"
                data-testid="checkin-submit"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {checkInForm.check_in_date > new Date().toISOString().split('T')[0] ? 'Jadwalkan Check-in' : 'Catat Check-in'}
              </Button>
            </form>

            {scheduledCheckins.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-semibold text-isk-text mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-isk-teal" />
                  Check-in Terjadwal ({scheduledCheckins.length})
                </h4>
                <div className="space-y-2">
                  {scheduledCheckins.map(sc => (
                    <div key={sc.id} className="flex items-center justify-between p-3 rounded-xl bg-blue-50 border border-blue-100">
                      <div>
                        <p className="text-sm font-medium text-isk-text">{sc.tenant_full_name}</p>
                        <p className="text-xs text-isk-muted">Kamar {sc.room_number} · Masuk: {sc.check_in_date instanceof Date ? sc.check_in_date.toISOString().slice(0,10) : String(sc.check_in_date).slice(0,10)}</p>
                        <p className="text-xs text-isk-muted">{sc.tenant_phone}</p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Batalkan jadwal check-in ${sc.tenant_full_name}?`)) return;
                          try {
                            const token = localStorage.getItem('token');
                            await axios.delete(`${BACKEND_URL}/api/admin/scheduled-checkins/${sc.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            setSuccess('Jadwal check-in dibatalkan');
                            fetchData();
                          } catch { setError('Gagal membatalkan jadwal'); }
                        }}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        Batalkan
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {canManualCheckout && activeTab === 'checkout' && (
          <div className="glass-card rounded-2xl p-6 max-w-xl">
            <h3 className="text-xl font-bold text-isk-text mb-6 flex items-center">
              <UserMinus className="w-5 h-5 mr-2 text-red-500" />
              Form Check-out Tenant
            </h3>

            <form onSubmit={handleCheckOut} className="space-y-4" data-testid="checkout-form">
              <div className="space-y-2">
                <Label>Pilih Kamar *</Label>
                <Select
                  value={checkOutForm.room_number}
                  onValueChange={(value) => setCheckOutForm({ ...checkOutForm, room_number: value })}
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="checkout-select-room">
                    <SelectValue placeholder="Pilih kamar terisi" />
                  </SelectTrigger>
                  <SelectContent>
                    {occupiedRooms.length > 0 ? (
                      occupiedRooms.map((room, idx) => (
                        <SelectItem key={`occupied-${room.room_number}-${idx}`} value={room.room_number}>
                          Kamar {room.room_number} - {room.tenant?.full_name || 'Terisi'}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>Tidak ada kamar terisi</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {checkOutForm.room_number && (
                <div className="p-4 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                  {(() => {
                    const selectedRoom = occupiedRooms.find(r => r.room_number === checkOutForm.room_number);
                    if (selectedRoom?.tenant) {
                      return (
                        <>
                          <p className="text-sm font-semibold text-isk-text mb-2">Info Tenant:</p>
                          <p className="text-sm text-isk-muted">Nama: {selectedRoom.tenant.full_name}</p>
                          <p className="text-sm text-isk-muted">HP: {selectedRoom.tenant.phone}</p>
                          <p className="text-sm text-isk-muted">Masuk: {formatDate(selectedRoom.tenant.check_in_date)}</p>
                          {selectedRoom.tenant.room_price && (
                            <p className="text-sm text-isk-teal font-semibold">Harga: {formatCurrency(selectedRoom.tenant.room_price)}/bulan</p>
                          )}
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="checkout_date">Tanggal Check-out *</Label>
                <Input
                  id="checkout_date"
                  type="date"
                  required
                  value={checkOutForm.check_out_date}
                  onChange={(e) => setCheckOutForm({ ...checkOutForm, check_out_date: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="checkout-input-date"
                />
              </div>

              <Button
                type="submit"
                disabled={occupiedRooms.length === 0}
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full py-6 font-semibold shadow-lg"
                data-testid="checkout-submit"
              >
                <UserMinus className="w-5 h-5 mr-2" />
                Catat Check-out
              </Button>
            </form>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-isk-text mb-6">Riwayat Check-in / Check-out</h3>

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="events-table">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Kamar</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Tenant</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">No HP</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Tgl Check-in</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-isk-text">Tgl Check-out</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-isk-text">Harga Kamar</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length > 0 ? (
                    events.map((event, index) => (
                      <tr key={index} className="border-b border-white/10 hover:bg-white/30 transition-colors">
                        <td className="py-3 px-4 text-sm text-isk-text font-medium">{event.room_number}</td>
                        <td className="py-3 px-4 text-sm text-isk-text">{event.tenant_full_name}</td>
                        <td className="py-3 px-4 text-sm text-isk-muted">{event.tenant_phone}</td>
                        <td className="py-3 px-4 text-sm">
                          <span className="flex items-center gap-1 text-green-700">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            {formatDate(event.event_date)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {event.checkout_date ? (
                            <span className="flex items-center gap-1 text-red-600">
                              <span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
                              {formatDate(event.checkout_date)}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">Masih menghuni</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-isk-teal font-semibold">
                          {event.room_price ? formatCurrency(event.room_price) : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-isk-muted">
                        Belum ada riwayat check-in/check-out
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Tenant Credentials Modal */}
      {tenantCredentials && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-isk-text">Akun Tenant Dibuat!</h3>
              <p className="text-sm text-isk-muted mt-1">Simpan kredensial ini untuk tenant</p>
            </div>

            <div className="space-y-4 bg-white/50 rounded-xl p-4">
              <div>
                <p className="text-xs text-isk-muted mb-1">Nomor HP (Username)</p>
                <p className="font-mono text-lg font-bold text-isk-text" data-testid="tenant-phone-display">
                  {tenantCredentials.phone}
                </p>
              </div>
              <div>
                <p className="text-xs text-isk-muted mb-1">Password</p>
                <p className="font-mono text-lg font-bold text-isk-teal" data-testid="tenant-password-display">
                  {tenantCredentials.password}
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-xl bg-yellow-50 border border-yellow-200">
              <p className="text-xs text-yellow-700">
                <strong>Penting:</strong> {tenantCredentials.note}
              </p>
            </div>

            <div className="mt-6 flex space-x-3">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`Nomor HP: ${tenantCredentials.phone}\nPassword: ${tenantCredentials.password}`);
                  setSuccess('Kredensial disalin ke clipboard!');
                  setTimeout(() => setSuccess(''), 2000);
                }}
                variant="outline"
                className="flex-1 border-isk-teal/30 text-isk-teal rounded-full"
              >
                Salin
              </Button>
              <Button
                onClick={() => setTenantCredentials(null)}
                className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full"
                data-testid="close-credentials-modal"
              >
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Check-in Modal */}
      {canVerify && editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-bold text-isk-text text-lg">Edit Data Check-in</h3>
                <p className="text-xs text-isk-muted">Kamar {editModal.room_number} · {propertyName}</p>
              </div>
              <button onClick={() => setEditModal(null)}><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nama Tenant</Label>
                  <Input value={editForm.tenant_full_name} onChange={e => setEditForm(f => ({...f, tenant_full_name: e.target.value}))} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Nomor HP</Label>
                  <Input value={editForm.tenant_phone} onChange={e => setEditForm(f => ({...f, tenant_phone: e.target.value}))} className="mt-1 rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tanggal Check-in</Label>
                  <Input type="date" value={editForm.event_date} onChange={e => setEditForm(f => ({...f, event_date: e.target.value}))} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Harga Kamar / Bulan</Label>
                  <Input type="number" value={editForm.room_price} onChange={e => setEditForm(f => ({...f, room_price: e.target.value}))} className="mt-1 rounded-xl" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold">Fasilitas Tambahan</Label>
                {[
                  { key: 'laundry', priceKey: 'laundry_price', label: 'Laundry' },
                  { key: 'parkir', priceKey: 'parkir_price', label: 'Parkir' },
                  { key: 'cleaning', priceKey: 'cleaning_price', label: 'Cleaning' },
                ].map(({ key, priceKey, label }) => (
                  <div key={key} className={`rounded-xl border-2 overflow-hidden ${editForm[key] ? 'border-isk-teal' : 'border-gray-200'}`}>
                    <label className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${editForm[key] ? 'bg-isk-teal/10' : 'bg-white/60'}`}>
                      <input type="checkbox" checked={!!editForm[key]} onChange={e => setEditForm(f => ({...f, [key]: e.target.checked}))} className="accent-isk-teal w-4 h-4" />
                      <span className={`text-sm font-medium ${editForm[key] ? 'text-isk-teal' : 'text-isk-muted'}`}>{label}</span>
                    </label>
                    {editForm[key] && (
                      <div className="px-3 pb-2 bg-isk-teal/5">
                        <Input type="number" min="0" value={editForm[priceKey]} onChange={e => setEditForm(f => ({...f, [priceKey]: e.target.value}))} placeholder={`Harga ${label} / bulan`} className="rounded-lg text-sm mt-1" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Co-Tenants */}
              {editCoTenants.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Penghuni Tambahan</Label>
                  {editCoTenants.map((ct, i) => (
                    <div key={i} className="p-3 rounded-xl bg-blue-50 border border-blue-100 space-y-2">
                      <p className="text-xs font-medium text-isk-muted">Penghuni {i + 2}</p>
                      <Input placeholder="Nama" value={ct.nama || ''} onChange={e => setEditCoTenants(prev => prev.map((c,j) => j===i ? {...c, nama: e.target.value} : c))} className="rounded-xl text-sm" />
                      <Input placeholder="Nomor HP" value={ct.phone || ''} onChange={e => setEditCoTenants(prev => prev.map((c,j) => j===i ? {...c, phone: e.target.value} : c))} className="rounded-xl text-sm" type="tel" />
                      <select value={ct.status || ''} onChange={e => setEditCoTenants(prev => prev.map((c,j) => j===i ? {...c, status: e.target.value} : c))}
                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-isk-teal focus:outline-none bg-white">
                        <option value="">Status / Hubungan</option>
                        <option value="Suami">Suami</option>
                        <option value="Istri">Istri</option>
                        <option value="Anak">Anak</option>
                        <option value="Keluarga">Keluarga</option>
                        <option value="Teman">Teman</option>
                        <option value="Lainnya">Lainnya</option>
                      </select>
                      <div className="flex items-center gap-2">
                        <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed cursor-pointer text-xs ${editCoKtpFiles[i] ? 'border-isk-teal bg-isk-teal/5 text-isk-teal' : 'border-gray-200 text-isk-muted hover:bg-gray-50'}`}>
                          <Upload className="w-3.5 h-3.5" />
                          <span className="truncate">{editCoKtpFiles[i] ? editCoKtpFiles[i].name : 'Ganti KTP'}</span>
                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setEditCoKtpFiles(prev => ({...prev, [i]: e.target.files?.[0]||null}))} />
                        </label>
                        {ct.ktp_url && <a href={`${BACKEND_URL}${ct.ktp_url}`} target="_blank" rel="noreferrer" className="text-xs text-isk-teal hover:underline px-2 py-1.5 border border-isk-teal/30 rounded-lg">Lihat</a>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-semibold">Dokumen Tenant Utama</Label>
                {[
                  { key: 'ktp', label: 'Foto KTP', file: editKtpFile, setFile: setEditKtpFile, currentUrl: editModal?.tenant?.ktp_photo_url },
                  { key: 'deposit', label: 'Bukti Deposit', file: editDepositFile, setFile: setEditDepositFile, currentUrl: editModal?.tenant?.bukti_deposit_url },
                ].map(({ key, label, file, setFile, currentUrl }) => (
                  <div key={key}>
                    <Label className="text-xs text-isk-muted">{label}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed cursor-pointer text-xs transition-colors ${file ? 'border-isk-teal bg-isk-teal/5 text-isk-teal' : 'border-gray-200 bg-gray-50 text-isk-muted hover:bg-gray-100'}`}>
                        <Upload className="w-4 h-4 shrink-0" />
                        <span className="truncate">{file ? file.name : `Ganti ${label}`}</span>
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
                      </label>
                      {currentUrl && (
                        <a href={resolveImg(currentUrl)} target="_blank" rel="noreferrer"
                          className="shrink-0 px-2 py-1.5 rounded-lg border border-isk-teal/30 text-isk-teal text-xs hover:bg-isk-teal/5">
                          Lihat
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditModal(null)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={editSubmitting} className="flex-1 bg-isk-teal text-white rounded-full">
                  {editSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OccupancyManagement;
